import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { DoorOpen, Loader2, MonitorSmartphone, PencilRuler } from "lucide-react";
import { useSetPageHeader } from "@/lib/page-header";
import { usePermissao } from "@/lib/use-permissao";
import { useIsMobile } from "@/lib/use-media-query";
import { cn } from "@/lib/cn";
import { trpc } from "@/lib/trpc";
import { NoAccessBlock } from "@/components/shared/NoAccessBlock";
import { AppointmentDetailDialog } from "@/components/dashboard2/AppointmentDetailDialog";
import { PlanoPagoSalas } from "@/components/salas/PlanoPagoSalas";
import { ListaAgora } from "@/components/salas/ListaAgora";
import { PlantaAoVivo } from "@/components/salas/planta/PlantaAoVivo";
import { useSalasAgora } from "@/components/salas/use-salas-agora";
import { AbaOcupacao } from "@/components/salas/ocupacao/AbaOcupacao";
import { DialogoDeSalas } from "@/components/salas/DialogoDeSalas";

const ROTULO_DA_ABA = { planta: "Planta", lista: "Lista", ocupacao: "Ocupação" } as const;

const botao =
  "inline-flex h-9 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 text-[13px] font-semibold text-zinc-700 hover:bg-zinc-50 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-muted";

/** O que acontece em cada sala agora: a planta ao vivo e a lista, com o modo recepção ao lado. */
export function Salas() {
  useSetPageHeader({ title: "Salas", subtitle: "O que acontece em cada sala agora" }, []);
  const podeVer = usePermissao("agenda.view");
  const podeEditar = usePermissao("settings.manage");
  const isMobile = useIsMobile();
  const s = useSalasAgora();
  const planta = trpc.planta.obter.useQuery(undefined, { enabled: s.temRecurso });
  const [params, setParams] = useSearchParams();
  const [aberta, setAberta] = useState<string | null>(null);
  const [gerenciando, setGerenciando] = useState(false);
  const detalhe = trpc.appointment.getById.useQuery({ id: aberta ?? "" }, { enabled: !!aberta });

  const temPlanta = !!planta.data?.salas.length;
  const pedida = params.get("aba");
  // Ocupação é só pra quem cuida da clínica; no celular a planta não cabe.
  const abas = [...(isMobile ? [] : (["planta"] as const)), "lista" as const, ...(podeEditar ? (["ocupacao"] as const) : [])];
  const aba = pedida === "ocupacao" && podeEditar ? "ocupacao" : isMobile || pedida === "lista" || (!pedida && !temPlanta) ? "lista" : "planta";

  const conteudo = () => {
    if (!s.temRecurso) return <PlanoPagoSalas />;
    if (!podeVer) return <NoAccessBlock description="As salas são visíveis para quem vê a agenda." />;
    if (s.carregando || planta.isLoading) return <Loader2 className="mx-auto mt-16 h-5 w-5 animate-spin text-zinc-400" />;
    if (!s.salas.length) {
      return (
        <Vazio texto="Nenhuma sala cadastrada ainda.">
          {podeEditar && <button type="button" onClick={() => setGerenciando(true)} className={botao}>Cadastrar salas</button>}
        </Vazio>
      );
    }
    return (
      <>
        <div className="flex flex-col-reverse gap-3 border-b border-zinc-200 sm:flex-row sm:items-end sm:justify-between dark:border-border">
          {abas.length > 1 && (
            <div className="flex gap-1" role="tablist" aria-label="Como ver as salas">
              {abas.map((a) => (
                <button
                  key={a}
                  type="button"
                  role="tab"
                  aria-selected={aba === a}
                  onClick={() => setParams({ aba: a }, { replace: true })}
                  className={cn(
                    "-mb-px border-b-2 px-3 py-3 text-[13px] font-medium transition-colors",
                    aba === a ? "border-brand-600 font-semibold text-brand-700 dark:border-primary dark:text-primary" : "border-transparent text-zinc-500 hover:text-zinc-800 dark:text-muted-foreground",
                  )}
                >
                  {ROTULO_DA_ABA[a]}
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-2 pb-2">
            {podeEditar && (
              <button type="button" onClick={() => setGerenciando(true)} className={botao}>
                <DoorOpen className="h-4 w-4" />
                Gerenciar salas
              </button>
            )}
            {podeEditar && !isMobile && (
              <Link to="/salas/planta" className={botao}>
                <PencilRuler className="h-4 w-4" />
                Editar planta
              </Link>
            )}
            <a href="/salas/recepcao" target="_blank" rel="noreferrer" className={botao}>
              <MonitorSmartphone className="h-4 w-4" />
              Modo recepção
            </a>
          </div>
        </div>
        {aba === "ocupacao" ? (
          <AbaOcupacao />
        ) : aba === "lista" ? (
          <ListaAgora salas={s.salas} porSala={s.porSala} semSala={s.semSala} onAbrir={setAberta} />
        ) : temPlanta ? (
          <PlantaAoVivo planta={planta.data!} salas={s.salas} porSala={s.porSala} onAbrir={setAberta} />
        ) : (
          <Vazio texto="A planta da clínica ainda não foi montada.">
            {podeEditar && <Link to="/salas/planta" className={botao}>Montar a planta</Link>}
          </Vazio>
        )}
      </>
    );
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-4 pb-10">
      {conteudo()}
      <AppointmentDetailDialog appointment={aberta ? ((detalhe.data as any) ?? null) : null} onClose={() => setAberta(null)} />
      {podeEditar && <DialogoDeSalas aberto={gerenciando} onAbertoChange={setGerenciando} />}
    </div>
  );
}

function Vazio({ texto, children }: { texto: string; children?: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-zinc-300 px-6 py-14 text-center dark:border-border">
      <p className="text-sm text-zinc-500 dark:text-muted-foreground">{texto}</p>
      {children}
    </div>
  );
}
